import { makeAutoObservable, runInAction, toJS } from "mobx";
import { esgService } from "../../services";
import { customDisclosureService } from "../../services/custom-disclosure.service";
import { esgReportBuilderStore } from "./esg-report-builder.store";
import { esgCollaborationStore } from "./esg-collaboration.store";
import { esgReportWorkflowStore } from "./esg-report-workflow.store";
import { resolveCodeForFramework } from "../../utils/exportHelpers";

class ESGDisclosureSelectionStore {
  availableDisclosures = [];
  loadingDisclosures = false;

  selectedCodes = [];
  customDisclosures = [];
  companyCustomDisclosures = [];
  isDirty = false;

  searchQuery = "";
  selectedCategories = [];
  showOnlySelected = false;

  disclosuresByFramework = new Map();
  metricMappings = [];
  metricMappingsCache = [];

  constructor() {
    makeAutoObservable(this);
  }

  // Compute
  get isUniversal() {
    return esgReportBuilderStore.framework === "UNIVERSAL";
  }

  get isCustom() {
    return esgReportBuilderStore.framework === "CUSTOM";
  }

  get masterMetricGroups() {
    if (!this.isUniversal) return [];
    return this.metricMappings
      .map((mm) => {
        const children = this.availableDisclosures.filter(
          (d) => d.masterMetricId === mm.masterMetricId,
        );
        if (children.length === 0) return null;
        return {
          code: mm.masterMetricId,
          title: mm.metricName,
          category: mm.category,
          framework: "Universal",
          inputType: mm.inputType,
          unitOptions: mm.defaultUnit ? [mm.defaultUnit] : [],
          frequency: mm.frequency,
          description: {
            summary: `Covers: ${mm.disclosureMappings.map((d) => `${d.framework} (${d.disclosureCode})`).join(", ")}`,
          },
          isMasterMetricGroup: true,
          masterMetricId: mm.masterMetricId,
          frameworks: children.map((c) => c.framework),
          children,
        };
      })
      .filter(Boolean);
  }

  get filteredDisclosures() {
    // CUSTOM framework: only show company custom disclosures
    // Standard frameworks: show standard disclosures + report-specific custom disclosures only
    let disclosures = this.isCustom
      ? [...this.companyCustomDisclosures]
      : [...this.availableDisclosures, ...this.customDisclosures];

    if (this.isUniversal && this.masterMetricGroups.length > 0) {
      const groupedCodeSet = new Set(
        this.masterMetricGroups.flatMap((g) => g.children.map((c) => c.code)),
      );
      disclosures = [
        ...this.masterMetricGroups,
        ...disclosures.filter((d) => !groupedCodeSet.has(d.code)),
      ];
    }

    // Apply search filter
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      disclosures = disclosures.filter(
        (d) =>
          d.code.toLowerCase().includes(query) ||
          d.title.toLowerCase().includes(query),
      );
    }

    // Apply category filter
    if (this.selectedCategories.length > 0) {
      disclosures = disclosures.filter((d) =>
        this.selectedCategories.includes(d.category),
      );
    }

    // Apply selection filter
    if (this.showOnlySelected) {
      disclosures = disclosures.filter((d) =>
        this.selectedCodes.includes(d.code),
      );
    }

    return disclosures;
  }

  get groupedByCategory() {
    const groups = {
      Environment: [],
      Social: [],
      Governance: [],
    };

    this.filteredDisclosures.forEach((d) => {
      if (groups[d.category]) {
        groups[d.category].push(d);
      }
    });

    return groups;
  }

  get groupedByCategoryForDelegate() {
    let groups = { ...this.groupedByCategory };

    Object.keys(groups).forEach((category) => {
      const newDisclosures = [];
      for (const d of groups[category]) {
        const delegated = esgCollaborationStore.delegatedDisclosures.get(
          d.code,
        );
        const selected = this.selectedCodes.includes(d.code);
        const pushed = newDisclosures.some((nd) => nd.code === d.code);
        if (!selected && !pushed && !delegated) newDisclosures.push(d);
      }
      groups[category] = newDisclosures;
    });

    return groups;
  }

  get selectedCount() {
    return this.selectedCodes.length + this.customDisclosures.length;
  }

  get selectedCodesByCategory() {
    const groups = {
      Environment: [],
      Social: [],
      Governance: [],
    };

    // Combine selected codes with disclosure-level delegated codes
    const delegatedCodes = Array.from(
      esgCollaborationStore.delegatedDisclosures.keys(),
    );
    const allCodes = [...new Set([...this.selectedCodes, ...delegatedCodes])];

    console.log("allCodes", allCodes);
    const allDisclosures = [
      ...this.availableDisclosures,
      ...this.customDisclosures,
      ...this.companyCustomDisclosures,
    ];

    allCodes.forEach((code) => {
      const found = allDisclosures.find((d) => d.code === code);
      const category =
        found?.category ||
        this.metricMappings.find((m) => m.masterMetricId === code)?.category;
      if (groups[category]) {
        groups[category].push(code);
      }
    });

    return groups;
  }

  get totalDisclosures() {
    return this.availableDisclosures.length;
  }

  get isSelectedAllDisclosures() {
    return (
      this.filteredDisclosures.length > 0 &&
      this.filteredDisclosures.every((d) => this.selectedCodes.includes(d.code))
    );
  }

  get availableExportFrameworks() {
    if (!this.isUniversal || this.selectedCodes.length === 0) return ["All"];

    const frameworkSet = new Set();

    this.selectedCodes.forEach((code) => {
      // Check if it's a masterMetricId
      const mapping = this.metricMappings.find(
        (m) => m.masterMetricId === code,
      );
      if (mapping) {
        mapping.disclosureMappings.forEach((d) =>
          frameworkSet.add(d.framework),
        );
      } else {
        // Individual disclosure
        const disclosure = this.availableDisclosures.find(
          (d) => d.code === code,
        );
        if (disclosure?.framework) {
          frameworkSet.add(disclosure.framework);
        }
      }
    });

    return ["All", ...Array.from(frameworkSet).sort()];
  }

  get filteredSelectedCodesByCategory() {
    if (
      !this.isUniversal ||
      esgReportWorkflowStore.reviewExportFramework === "All"
    ) {
      return this.selectedCodesByCategory;
    }

    const targetFramework = esgReportWorkflowStore.reviewExportFramework;
    const result = { Environment: [], Social: [], Governance: [] };
    const disclosureMap = new Map(
      this.availableDisclosures.map((d) => [d.code, d]),
    );

    Object.entries(this.selectedCodesByCategory).forEach(
      ([category, codes]) => {
        codes.forEach((code) => {
          const disclosure = disclosureMap.get(code);
          const resolved = resolveCodeForFramework(
            code,
            disclosure,
            targetFramework,
            this.metricMappings,
          );
          if (resolved) {
            result[category].push(code);
          }
        });
      },
    );

    return result;
  }

  // Actions
  setSearchQuery = (query) => (this.searchQuery = query);

  toggleCategory = (category) => {
    const index = this.selectedCategories.indexOf((c) => c === category);
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
    } else {
      this.selectedCategories.push(category);
    }
  };

  setShowOnlySelected = (v) => (this.showOnlySelected = v);

  toggleDisclosure = (code) => {
    const index = this.selectedCodes.indexOf(code);
    if (index > -1) {
      this.selectedCodes.splice(index, 1);
    } else {
      this.selectedCodes.push(code);
    }
    this.isDirty = true;
  };

  selectAll = () => {
    const arr = this.filteredDisclosures.map((d) => d.code);
    const uniqueArr = [...new Set(arr)];
    this.selectedCodes = uniqueArr;
    this.isDirty = true;
  };

  unSelectAll = () => {
    this.selectedCodes = [];
    this.isDirty = true;
  };

  selectByCategory = (category) => {
    const categoryCodes = this.filteredDisclosures
      .filter((d) => d.category === category)
      .map((d) => d.code);

    categoryCodes.forEach((code) => {
      if (!this.selectedCodes.includes(code)) {
        this.selectedCodes.push(code);
      }
    });
    this.isDirty = true;
  };

  unselectByCategory = (category) => {
    const categoryCodes = this.filteredDisclosures
      .filter((d) => d.category === category)
      .map((d) => d.code);

    this.selectedCodes = this.selectedCodes.filter(
      (code) => !categoryCodes.includes(code),
    );
    this.isDirty = true;
  };

  addCustomDisclosure = (disclosure) => {
    this.customDisclosures.push({
      ...disclosure,
      framework: esgReportBuilderStore.framework,
      isCustom: true,
      id: `custom_${Date.now()}`,
    });
    this.isDirty = true;
  };

  removeCustomDisclosure = (id) => {
    const disclosure = this.customDisclosures.find((d) => d.id === id);
    if (disclosure) {
      this.customDisclosures = this.customDisclosures.filter(
        (d) => d.id !== id,
      );
      this.selectedCodes = this.selectedCodes.filter(
        (c) => c !== disclosure.code,
      );
      this.isDirty = true;
    }
  };

  loadDisclosuresForFramework = async (framework) => {
    const cached = this.disclosuresByFramework.get(framework);
    if (cached) {
      runInAction(() => {
        this.availableDisclosures = cached;
      });
      return;
    }

    runInAction(() => {
      this.loadingDisclosures = true;
    });

    try {
      const apiFramework = framework === "UNIVERSAL" ? "All" : framework;
      const data = await esgService.getDisclosures({
        framework: apiFramework,
        category: "All",
        page: 1,
        limit: 10000,
      });

      runInAction(() => {
        const disclosures = data?.disclosures || [];
        this.availableDisclosures = disclosures;
        this.disclosuresByFramework.set(framework, disclosures);
      });
    } finally {
      runInAction(() => {
        this.loadingDisclosures = false;
      });
    }
  };

  loadMetricMappings = async () => {
    if (this.metricMappingsCache?.length) {
      runInAction(() => {
        this.metricMappings = this.metricMappingsCache;
      });
      return;
    }

    try {
      const data = await esgService.getMetricMappings();
      runInAction(() => {
        const mappings = data || [];
        this.metricMappings = mappings;
        this.metricMappingsCache = mappings;
      });
    } catch (error) {
      console.error("Failed to load metric mappings:", error);
    }
  };

  loadCompanyCustomDisclosures = async () => {
    try {
      const response = await customDisclosureService.list({
        isActive: "true",
        limit: 1000,
      });
      runInAction(() => {
        this.companyCustomDisclosures = (response?.disclosures || []).map(
          (cd) => ({
            ...cd,
            framework: "CUSTOM",
            isCustom: true,
            description: cd.description || "",
          }),
        );
      });
    } catch (error) {
      console.error("Failed to load custom disclosures:", error);
    }
  };

  loadSelections = (data) => {
    this.selectedCodes = data.selectedDisclosures || [];
    this.customDisclosures =
      data.customDisclosures.map((cd) => ({ ...cd, isCustom: true })) || [];
    this.isDirty = false;
  };

  resetDisclosure = () => {
    runInAction(() => {
      this.selectedCodes = [];
      this.customDisclosures = [];
      this.companyCustomDisclosures = [];
      this.searchQuery = "";
      this.selectedCategories = [];
      this.showOnlySelected = false;
      this.isDirty = false;
      this.metricMappings = [];
      this.metricMappingsCache = [];
    });
  };
}

export const esgDisclosureSelectionStore = new ESGDisclosureSelectionStore();
